class TrPageLoader extends HTMLElement {
	selectors = {
		value: '[data-tr-js-page-loader-value]'
	};

	attributes = {};

	stateClasses = {
		isVisible: 'is-visible',
		isHidden: 'is-hidden'
	};

	connectedCallback() {
		this.valueElement = this.querySelector(this.selectors.value);
		this.storageKey = 'trPageLoaderViewed';
		this.animationFrame = null;
		this.hideTimeout = null;
		this.removeTimeout = null;
		this.currentProgress = 0;
		this.targetProgress = 92;
		this.isLoaded = document.readyState === 'complete';
		this.isSkipped = false;
		this.extraDuration = Number(this.dataset.trJsPageLoaderExtraDuration) || 0;
		this.exitDuration = this.getExitDuration();

		this.init();
		if (this.isSkipped) return;

		this.bindEvents();
	}

	disconnectedCallback() {
		window.removeEventListener('load', this.onWindowLoad);

		this.destroy();
	}

	init() {
		if (this.hasBeenViewed()) {
			this.isSkipped = true;
			this.removeLoader();
			return;
		}

		this.classList.add(this.stateClasses.isVisible);
		this.updateProgress(0);

		if (this.isLoaded) {
			this.finish();
			return;
		}

		this.animate();
	}

	bindEvents() {
		window.addEventListener('load', this.onWindowLoad, { once: true });
	}

	onWindowLoad = () => {
		this.finish();
	};

	animate = () => {
		this.currentProgress += (this.targetProgress - this.currentProgress) * 0.055;

		if (this.currentProgress < this.targetProgress) {
			this.updateProgress(this.currentProgress);
			this.animationFrame = requestAnimationFrame(this.animate);
		}
	};

	finish() {
		this.targetProgress = 100;
		cancelAnimationFrame(this.animationFrame);
		this.completeProgress();
	}

	completeProgress = () => {
		this.currentProgress += (100 - this.currentProgress) * 0.18;

		if (this.currentProgress < 99.5) {
			this.updateProgress(this.currentProgress);
			this.animationFrame = requestAnimationFrame(this.completeProgress);
			return;
		}

		this.updateProgress(100);
		this.hideTimeout = window.setTimeout(() => this.hide(), this.extraDuration);
	};

	updateProgress(progress) {
		const roundedProgress = Math.round(progress);
		const progressValue = `${Math.min(100, Math.max(0, progress))}%`;

		this.style.setProperty('--trPageLoaderProgress', progressValue);
		this.setAttribute('aria-valuenow', String(roundedProgress));
		this.valueElement.textContent = String(roundedProgress);
	}

	hide() {
		this.saveViewedState();
		this.classList.add(this.stateClasses.isHidden);
		document.documentElement.classList.remove('tr-page-loader-enabled');
		this.removeTimeout = window.setTimeout(() => this.removeLoader(), this.exitDuration);
	}

	getExitDuration() {
		const durationValue = window.getComputedStyle(this).getPropertyValue('--trPageLoaderExitDuration');
		const duration = Number.parseFloat(durationValue);

		return Number.isNaN(duration) ? 900 : duration;
	}

	hasBeenViewed() {
		try {
			return window.sessionStorage.getItem(this.storageKey) === 'true';
		} catch (error) {
			return false;
		}
	}

	saveViewedState() {
		try {
			window.sessionStorage.setItem(this.storageKey, 'true');
		} catch (error) {
			return;
		}
	}

	removeLoader() {
		document.documentElement.classList.remove('tr-page-loader-enabled');
		this.remove();
	}

	destroy() {
		cancelAnimationFrame(this.animationFrame);
		window.clearTimeout(this.hideTimeout);
		window.clearTimeout(this.removeTimeout);
		document.documentElement.classList.remove('tr-page-loader-enabled');
	}
}

if (!customElements.get('tr-page-loader')) {
	customElements.define('tr-page-loader', TrPageLoader);
}
